
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, FileText, Eye } from 'lucide-react';
import { toast } from 'sonner';

interface PDFTemplateUploaderProps {
  onPDFUploaded: (file: File, preview: string) => void;
  className?: string;
}

const PDFTemplateUploader: React.FC<PDFTemplateUploaderProps> = ({
  onPDFUploaded,
  className
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/pdf') {
        handleFileUpload(file);
      } else {
        toast.error('Please upload a PDF file');
      }
    }
  };

  const handleFileUpload = async (file: File) => {
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    setIsLoading(true);
    try {
      // Create a preview URL for the PDF
      const previewUrl = URL.createObjectURL(file);
      
      // In a real implementation, we would upload the file to a server
      // For now, we'll just pass the file and preview URL to the parent component
      onPDFUploaded(file, previewUrl);
      
      toast.success('PDF template uploaded successfully');
    } catch (error) {
      console.error('Error uploading PDF template:', error);
      toast.error('Failed to upload PDF template');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className={`${className} ${isDragging ? 'ring-2 ring-primary' : ''}`}>
      <CardContent className="p-4">
        <div
          className="border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <FileText className="h-12 w-12 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground mb-2">
            Drag and drop or click to upload a PDF certificate template
          </p>
          <Label
            htmlFor="pdf-upload"
            className="bg-primary text-primary-foreground hover:bg-primary/90 py-2 px-4 rounded cursor-pointer flex items-center"
          >
            <Upload className="h-4 w-4 mr-2" />
            Choose PDF
          </Label>
          <Input
            id="pdf-upload"
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className="hidden"
            disabled={isLoading}
          />
          {isLoading && <p className="mt-2 text-sm">Uploading...</p>}
        </div>
      </CardContent>
    </Card>
  );
};

export default PDFTemplateUploader;
